import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {clsx} from 'clsx'
import {useCallback, useEffect, useRef} from 'react'
import styles from './PullRequestMiniHeader.module.css'
import {StateIcon} from './StateIcon'
import {useHeaderPageData} from '../page-data/loaders/use-header-page-data'
import type {HeaderPageData} from '../page-data/payloads/header'
import {getRouteConfig} from '../routes/route-manager'
import {useMergeStatusButtonData} from '../hooks/use-merge-status-button-data'
import {DockedPanel, useIsDockedPanelOpen, useDockedPanelActions} from '../contexts/DockedPanelContext'
import {MergeStatusButtonWrapper} from '@github-ui/mergebox'
import {OpenCommentsPanelButton} from './toolbar/OpenCommentsPanelButton'
import type {PullRequestHeaderProps} from './PullRequestHeader'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useFeatureState, useIsOverviewEnabled} from '../hooks/use-feature-state'
import {FeatureState} from '../page-data/payloads/header'
import {isLoggedIn, getCurrentUserLogin} from '@github-ui/client-env'
import {ReviewMenuButton} from './toolbar/ReviewMenuButton'
import {OpenCopilotChatPanelButton} from './toolbar/OpenCopilotChatPanelButton'
import {OpenDockedPanelButton} from './toolbar/OpenDockedPanelButton'
import {useUnifiedBatchCount} from '../stores/layout-selectors'
import {MiniHeaderTabSwitcher} from './MiniHeaderTabSwitcher'
import {HeaderDivider} from './HeaderDivider'
import {ManageBatchHeaderButton} from './toolbar/ManageBatchHeaderButton'
import {useLayoutRouteData} from '../page-data/loaders/use-layout-route-data'
import {PullRequestCopyLinkButton} from './PullRequestCopyLinkButton'

export type PullRequestMiniHeaderProps = PullRequestHeaderProps

export function PullRequestMiniHeader({activeRoute, ...initialData}: PullRequestMiniHeaderProps) {
  const {helpUrl} = useLayoutRouteData()
  const {data: headerData} = useHeaderPageData({...initialData, helpUrl})
  return <PullRequestMiniHeaderContent {...headerData} activeRoute={activeRoute} />
}

export type PullRequestMiniHeaderContentProps = Omit<HeaderPageData, 'helpUrl'> & {
  activeRoute: PullRequestHeaderProps['activeRoute']
}

export function PullRequestMiniHeaderContent({
  activeRoute,
  pullRequest,
  repository,
  mergeStatusButtonData,
  stack,
  urls,
}: PullRequestMiniHeaderContentProps) {
  const currentUserLogin = getCurrentUserLogin()
  const isPullRequestStacksEnabled = useFeatureState('pullRequestStacks') === FeatureState.Enabled
  const overviewEnabled = useIsOverviewEnabled()
  const {dockedPanelSupported} = getRouteConfig(activeRoute, overviewEnabled)
  const validatedMergeData = useMergeStatusButtonData(pullRequest.state, mergeStatusButtonData)
  const isDockedMode = !!dockedPanelSupported
  const dockedPanelActions = useDockedPanelActions()
  const mergeStatusButtonRef = useRef<HTMLButtonElement>(null)
  const hasValidatedMergeData = validatedMergeData !== null
  const batchCount = useUnifiedBatchCount()
  const {helpUrl} = useLayoutRouteData()

  const handleToggleDockedMergePanel = useCallback(() => {
    dockedPanelActions.togglePanel(DockedPanel.MergeStatus, {returnFocusTo: mergeStatusButtonRef.current})
  }, [dockedPanelActions])

  const isMergeStatusPanelOpen = useIsDockedPanelOpen(DockedPanel.MergeStatus)

  useEffect(() => {
    if (isDockedMode && isMergeStatusPanelOpen && !hasValidatedMergeData) {
      dockedPanelActions.closePanel()
    }
  }, [isDockedMode, isMergeStatusPanelOpen, hasValidatedMergeData, dockedPanelActions])

  const showManageBatchHeaderButton = isDockedMode && isFeatureEnabled('unified_batch_pr_comments') && batchCount > 0

  return (
    <div className={styles.miniHeaderInner}>
      <MiniHeaderTabSwitcher activeRoute={activeRoute} pullRequest={pullRequest} repository={repository} urls={urls} />
      <HeaderDivider />
      <div className={styles.metadata}>
        <StateIcon state={pullRequest.state} />
        <span className={clsx(styles.number, 'f5 fgColor-muted')}>#{pullRequest.number}</span>
        <a href="#top" className={clsx(styles.title, 'f5 text-bold fgColor-default')}>
          <VerifiedHTMLText className="markdown-title" html={pullRequest.titleHtml} />
        </a>
        <PullRequestCopyLinkButton
          owner={repository.ownerLogin}
          repository={repository.name}
          number={pullRequest.number}
          title={pullRequest.title}
        />
      </div>
      <div className={styles.actions}>
        {isLoggedIn() && validatedMergeData && (
          <MergeStatusButtonWrapper
            channels={validatedMergeData.channels}
            headSha={validatedMergeData.headSha}
            helpUrl={helpUrl}
            viewerLogin={validatedMergeData.viewerLogin}
            isDockedMode={isDockedMode}
            onToggleDockedPanel={handleToggleDockedMergePanel}
            mergeStatusButtonRef={mergeStatusButtonRef}
            variant="invisible"
            size="medium"
            buttonClasses={clsx('hide-sm hide-md hide-lg', styles.hideLgPlus)}
            showTrailingArrow
          />
        )}
        {currentUserLogin &&
          (showManageBatchHeaderButton ? (
            <ManageBatchHeaderButton
              className="tmp-ml-2"
              count={batchCount}
              entrySurface="sticky_header_manage_batch_button"
              size="medium"
            />
          ) : (
            <ReviewMenuButton
              className="tmp-ml-2"
              currentUserLogin={currentUserLogin}
              pullRequest={pullRequest}
              headRefOid={pullRequest.headSha}
              repository={repository}
              overlayPortal="external"
              redirectOnMutation={!(activeRoute === 'conversation' || activeRoute === 'overview')}
              stackBaseBranch={isPullRequestStacksEnabled ? stack?.baseBranch : undefined}
              size="medium"
            />
          ))}
        <HeaderDivider className="hide-sm tmp-ml-3" />
        <OpenCommentsPanelButton
          className="hide-sm hide-md"
          buttonClasses={styles.squareButton}
          showMarkerCounts={false}
          size="medium"
        />
        <OpenCopilotChatPanelButton size="medium" variant="invisible" className="hide-sm hide-md" />
        <OpenDockedPanelButton size="medium" className="tmp-ml-2" />
      </div>
    </div>
  )
}
